import { Beef, BookOpen, CalendarDays, LayoutDashboard, Menu, Settings, ShoppingCart, Target, User, UserPlus, Users, Utensils, Wine, X } from 'lucide-react';
import React from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import BrandLogo from './BrandLogo';
import FeedbackButton from './FeedbackButton';
import ProfileSwitcher from './ProfileSwitcher';

const Layout: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);

  const navItems = [
    { name: 'Dashboard', icon: LayoutDashboard, path: '/app' },
    { name: 'Coaching', icon: Target, path: '/app/coaching' },
    { name: 'Recipes', icon: Utensils, path: '/app/recipes' },
    { name: 'Meal Plan', icon: CalendarDays, path: '/app/meal-plan' },
    { name: 'Groceries', icon: ShoppingCart, path: '/app/groceries' },
    { name: 'Kitchen & Pantry', icon: Beef, path: '/app/pantry' },
    { name: 'Beverages', icon: Wine, path: '/app/beverages' },
    { name: 'Education', icon: BookOpen, path: '/app/education' },
    { name: 'Upgrades', icon: UserPlus, path: '/app/upgrades' },
    // Admin nav - only shown in dev mode
    ...(import.meta.env.DEV ? [{ name: 'Admin', icon: Settings, path: '/app/admin' }] : []),
  ];

  return (
    <div className="min-h-screen bg-sage-50 text-stone-800 font-sans flex flex-col md:flex-row">
      {/* Mobile Header */}
      <div className="md:hidden bg-sage-700 text-white p-4 flex justify-between items-center sticky top-0 z-50 shadow-md">
        <div className="flex items-center gap-3">
          <div className="bg-sage-50 rounded p-0.5">
            <BrandLogo className="w-8 h-8" />
          </div>
          <h1 className="text-lg font-semibold tracking-wide leading-tight">Health Beyond<br />Weight Loss™</h1>
        </div>
        <div className="flex items-center gap-3">
          <ProfileSwitcher compact />
          <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
            {isMobileMenuOpen ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {/* Sidebar Navigation */}
      <aside className={`
        fixed md:sticky top-0 h-screen w-64 bg-sage-800 text-white flex-col transition-transform transform z-40 shadow-xl
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0
      `}>
        <div className="p-6 border-b border-sage-700 flex items-center gap-3">
          <div className="bg-sage-50 rounded-lg p-1 shrink-0">
            <BrandLogo className="w-10 h-10" />
          </div>
          <div>
            <h1 className="text-lg font-bold leading-tight">Health Beyond<br /><span className="text-sage-300">Weight Loss™</span></h1>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto py-4">
          <ul className="space-y-1 px-3">
            {navItems.map((item) => (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${isActive ? 'bg-sage-600 text-white shadow-md' : 'text-sage-100 hover:bg-sage-700'
                    }`
                  }
                >
                  <item.icon size={20} />
                  <span className="font-medium">{item.name}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto h-[calc(100vh-64px)] md:h-screen scroll-smooth">
        {/* Desktop Top Bar */}
        <div className="hidden md:flex items-center justify-end gap-4 px-8 py-3 sticky top-0 bg-sage-50/95 backdrop-blur-sm border-b border-sage-200 z-30">
          <NavLink
            to="/app/profile-settings"
            className={({ isActive }) =>
              `flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${isActive ? 'bg-sage-200 text-sage-900' : 'text-sage-600 hover:text-sage-800 hover:bg-sage-100'}`
            }
          >
            <User size={16} />
            <span>My Account</span>
          </NavLink>
          <NavLink
            to="/app/household-settings"
            className={({ isActive }) =>
              `flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${isActive ? 'bg-sage-200 text-sage-900' : 'text-sage-600 hover:text-sage-800 hover:bg-sage-100'}`
            }
          >
            <Users size={16} />
            <span>Household</span>
          </NavLink>
          <ProfileSwitcher />
        </div>

        <div className="p-4 md:p-8">
          <Outlet />
        </div>
      </main>
      <FeedbackButton />
    </div>
  );
};

export default Layout;